import React, { SyntheticEvent, useEffect, useState } from 'react';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  School as SchoolIcon,
} from '@mui/icons-material';
import {
  Box,
  ClassNameMap,
  IconButton,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import axios, { AxiosError } from 'axios';
import {
  deleteFile,
  getFilesPerPage,
  postCreateFile,
  getFileSignedUrl,
} from '../../../requests/api/file';
import { getSubjects } from '../../../requests/api/subjects';
import { getPrograms } from '../../../requests/api/sections';
import { SubjectData } from '../../Program/ProgramListView/types';
import { FILES_UI, FileDataModal } from './types';
import UpsertFileModal from './Modals/UpsertFileModal';
import Header from '../../../components/Layout/Header';
import Page from '../../../components/Layout/Page';
import CustomTable from '../../../components/General/CustomTable';
import handleApiResponse from '../../../utils/handleApiResponse';
import EmptyTable from '../../../components/General/EmptyTable';
import DeleteModal from '../../../components/General/DeleteModal';
import CircularProgressComponent from '../../../components/Loading/CircularProgressComponent';
import { useYear } from '../../../hooks/useYear';
import useStyles from './styles';

const buttonActions = (
  classes: ClassNameMap<string>,
  fileId: string,
  seeFunction: () => void,
  openDeleteModal: ({ open, id }: { open: boolean, id: string }) => void,
) => (<Box>
  <IconButton onClick={seeFunction}>
    <VisibilityIcon className={classes.icons} />
  </IconButton>
  <IconButton onClick={() => openDeleteModal({ id: fileId, open: true })}>
    <DeleteIcon className={classes.icons} />
  </IconButton>
</Box>);

function FilesListView() {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { selectedYear } = useYear();

  const [allFiles, setAllFiles] = useState({
    items: [],
    totalItems: 0,
  });
  const [filesUI, setFilesUI] = useState(FILES_UI);
  const [modalAddEditAFile, setModalAddEditAFile] = useState(
    { id: '', open: false },
  );
  const [modalDeleteAFile, setModalDeleteAFile] = useState({ id: '', open: false });
  const [modalValues, setModalValues] = useState<FileDataModal>({
    subjectId: '',
    programId: '',
    name: '',
    file: undefined,
  });
  const [updateTable, setUpdateTable] = useState(false);
  const [showLoading, setShowLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState({
    files: false,
    subjects: false,
    programs: false,
  });
  const [filesFilters, setFilesFilters] = useState({
    page: 0,
    rowsPerPage: 100,
    searchValue: '',
    subjectId: '',
    programId: '',
    year: selectedYear,
  });

  const handleSeeFile = async (fileId: string) => {
    try {
      const { url } = await getFileSignedUrl(fileId);
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      const e = err as AxiosError;
      handleApiResponse(enqueueSnackbar, e, false);
    }
  };

  useEffect(() => {
    const source = axios.CancelToken.source();
    const getAllSubjects = async () => {
      try {
        const { subjects } = await getSubjects(source.token);
        const auxSubjects = subjects.map((s: SubjectData) => ({ id: s.id, name: s.name }));

        setFilesUI(FILES_UI.map((ui) => {
          if (ui.label === 'Asignatura') {
            ui.selectFields = auxSubjects;
          }
          return ui;
        }));
      } catch (err) {
        const e = err as AxiosError;
        handleApiResponse(enqueueSnackbar, e, false);
      } finally {
        setHasLoaded((prevState) => ({ ...prevState, subjects: true }));
      }
    };

    getAllSubjects();
    return () => {
      source.cancel();
    };
  }, []);

  useEffect(() => {
    const source = axios.CancelToken.source();
    const getAllPrograms = async () => {
      try {
        if (!modalValues.subjectId) {
          setFilesUI((prev) => prev.map((ui) => (
            ui.label === 'Programa' ? { ...ui, selectFields: [] as { id: string, name: string }[], valueToRetrieveFromSelect: 'id' } : ui
          )));
          return;
        }
        const { programs } = await getPrograms(modalValues.subjectId);
        const options = programs.map(
          (p: { id: string, name: string }) => ({ id: p.id, name: p.name }),
        );
        setFilesUI((prev) => prev.map((ui) => (
          ui.label === 'Programa' ? { ...ui, selectFields: options, valueToRetrieveFromSelect: 'id' } : ui
        )));
      } catch (err) {
        const e = err as AxiosError;
        handleApiResponse(enqueueSnackbar, e, false);
      } finally {
        setHasLoaded((prevState) => ({ ...prevState, programs: true }));
      }
    };

    getAllPrograms();
    return () => {
      source.cancel();
    };
  }, [modalValues.subjectId]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    const getAllFiles = async () => {
      try {
        const { rows, count } = await getFilesPerPage({
          page: filesFilters.page,
          limit: filesFilters.rowsPerPage,
          searchValue: filesFilters.searchValue,
          programId: filesFilters.programId,
          year: selectedYear,
        }, source.token);

        const filesWithActions = rows.map((file: {
          id: string,
          fileUrl?: string,
          program?: { programName?: string },
        }) => ({
          ...file,
          programName: file?.program?.programName,
          actions: buttonActions(
            classes, file.id,
            () => handleSeeFile(file.id),
            () => setModalDeleteAFile({ id: file.id, open: true }),
          ),
        }));

        setAllFiles({ items: filesWithActions, totalItems: count });
      } catch (err) {
        const e = err as AxiosError;
        handleApiResponse(enqueueSnackbar, e, false);
      } finally {
        setHasLoaded((prevState) => ({ ...prevState, files: true }));
      }
    };

    getAllFiles();
    return () => {
      source.cancel();
    };
  }, [
    filesFilters.page,
    filesFilters.rowsPerPage,
    filesFilters.searchValue,
    filesFilters.programId,
    updateTable,
    selectedYear,
  ]);

  useEffect(() => {
    if (Object.values(hasLoaded).every((elem) => elem === true)) {
      setShowLoading(false);
    }
  }, [hasLoaded.subjects, hasLoaded.programs, hasLoaded.files]);

  const handleCloseModal = (setFunction: React.Dispatch<React.SetStateAction<{
    id: string;
    open: boolean;
  }>>) => {
    setFunction(() => ({ id: '', open: false }));
    setModalValues({
      programId: '',
      subjectId: '',
      name: '',
      file: undefined,
    });
    setUpdateTable(!updateTable);
  };

  const addAFile = async () => {
    try {
      if (!modalValues.name) {
        enqueueSnackbar('Debes ingresar un nombre para el archivo.', {
          variant: 'warning',
        });
        return;
      }

      if (!modalValues.programId) {
        throw Error('Programa no seleccionado');
      }

      if (!modalValues.file) {
        enqueueSnackbar('Debes subir un archivo.', {
          variant: 'warning',
        });
        return;
      }

      const response = await postCreateFile({
        programId: modalValues.programId,
        name: modalValues.name as string,
        file: modalValues.file,
      });
      handleApiResponse(enqueueSnackbar, response, true);
      handleCloseModal(setModalAddEditAFile);
    } catch (err) {
      const e = err as AxiosError;
      handleApiResponse(enqueueSnackbar, e, false);
    }
  };

  const deleteAFile = async (fileId: string) => {
    try {
      const response = await deleteFile(fileId);
      handleApiResponse(enqueueSnackbar, response, true);
      handleCloseModal(setModalDeleteAFile);
    } catch (err) {
      const e = err as AxiosError;
      handleApiResponse(enqueueSnackbar, e, false);
    }
  };

  const handleFieldChange = (
    e: React.ChangeEvent<HTMLInputElement> | SyntheticEvent<Element, Event> | Date | File,
    source: string,
  ) => {
    if (e instanceof File) {
      setModalValues((prevState) => ({ ...prevState, [source]: e }));
      return;
    }

    const event = e as React.ChangeEvent<HTMLInputElement>;
    const value = event?.target?.value ?? '';

    if (source === 'subjectId') {
      setModalValues((prevState) => ({ ...prevState, subjectId: value, programId: '' }));
      setFilesUI((prev) => prev.map((ui) => (
        ui.label === 'Programa' ? { ...ui, selectFields: [] as { id: string, name: string }[], valueToRetrieveFromSelect: 'id' } : ui
      )));
      return;
    }

    setModalValues((prevState) => ({ ...prevState, [source]: value }));
  };

  const handleChangeSearchValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilesFilters((prevState) => ({ ...prevState, searchValue: e.target.value }));
    if (filesFilters.page !== 0) {
      setFilesFilters((prevState) => ({ ...prevState, page: 0 }));
    }
  };

  return (
    <Page title="Archivos">
      <Header
        title='Archivos'
        icon={<SchoolIcon fontSize="large" />}
        buttons={!showLoading ? [
          { text: 'Crear archivo', icon: <AddIcon />, onClick: () => setModalAddEditAFile((prevState) => ({ ...prevState, open: true })) },
        ] : undefined}
        search={!showLoading
          ? { text: 'Buscar un archivo...', value: filesFilters.searchValue || '', onChangeSearchValue: handleChangeSearchValue }
          : undefined}
      />
      {showLoading ? <CircularProgressComponent /> : <Box mt={3}>
        {allFiles.totalItems !== 0
          && <CustomTable
            headers={FILES_UI.filter((elem: { showOnTable: boolean }) => elem.showOnTable)}
            data={{ values: allFiles.items, count: allFiles.totalItems }}
            reduxTableParameters={filesFilters}
            setReduxTableParameters={setFilesFilters}
          />}
        {allFiles.totalItems === 0 && <EmptyTable />}
      </Box>}
      <UpsertFileModal
        modalAddEdit={modalAddEditAFile}
        setModalAddEdit={setModalAddEditAFile}
        handleCloseModal={handleCloseModal}
        modalValues={modalValues}
        setModalValues={setModalValues}
        handleFieldChange={handleFieldChange}
        itemsUI={filesUI}
        addFunction={addAFile}
      />
      <DeleteModal
        title={'archivo'}
        modalDelete={modalDeleteAFile}
        setModalDelete={setModalDeleteAFile}
        handleCloseModal={handleCloseModal}
        deleteFunction={deleteAFile}
      />
    </Page>
  );
}

export default FilesListView;
